import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  LayoutDashboard,
  History,
  Megaphone,
  Trophy,
  Gift,
  LogOut,
} from "lucide-react";
import { cn } from "../lib/utils";
import { branding } from "../lib/branding";
import RegulationGate from "../components/RegulationGate";

const navItems = [
  { to: "/parceiro", icon: LayoutDashboard, label: "Início", end: true },
  { to: "/parceiro/historico", icon: History, label: "Histórico" },
  { to: "/parceiro/campanhas", icon: Megaphone, label: "Campanhas" },
  { to: "/parceiro/ranking", icon: Trophy, label: "Ranking" },
  { to: "/parceiro/resgates", icon: Gift, label: "Resgates" },
];

const PartnerLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-20 lg:pb-0">
      {/* Top header */}
      <RegulationGate />
      <header className="sticky top-0 z-40 px-4 py-3 sm:px-6 lg:pl-72 lg:pr-8" style={{ backgroundColor: branding.headerBackground, color: branding.headerText }}>
        <div className="mx-auto flex w-full max-w-4xl items-center justify-between gap-4">
          <img src="/pin-clube-icon.png" alt={branding.name} className="h-11 w-11 shrink-0 rounded-lg lg:hidden" />
          <div className="min-w-0 flex-1">
            <p className="text-sm" style={{ color: branding.headerMuted }}>Olá,</p>
            <h1 className="truncate font-heading text-lg font-bold">
              {user?.nome}
            </h1>
          </div>
          <button
            onClick={handleLogout}
            className="p-2 rounded-lg hover:bg-white/15 transition-colors"
            aria-label="Sair"
            title="Sair"
            data-testid="logout-btn"
          >
            <LogOut className="h-5 w-5" />
          </button>
        </div>
      </header>

      {/* Main content */}
      <main className="w-full px-4 py-6 sm:px-6 lg:pl-72 lg:pr-8">
        <div className="mx-auto w-full max-w-4xl">
          <Outlet />
        </div>
      </main>

      {/* Bottom navigation - mobile only */}
      <nav aria-label="Menu mobile do parceiro" className="fixed bottom-0 left-0 right-0 z-40 border-t border-slate-200 bg-white safe-area-bottom lg:hidden">
        <div className="grid grid-cols-5 items-center gap-1 px-2 py-2">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  "flex min-w-0 flex-col items-center gap-1 rounded-lg px-1 py-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary",
                  isActive
                    ? "bg-primary text-white"
                    : "bg-orange-50 text-orange-800 hover:bg-orange-100 hover:text-orange-900"
                )
              }
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="h-5 w-5" />
              <span className="text-[11px] font-medium leading-4">{item.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>

      {/* Desktop sidebar - shown on lg+ */}
      <aside className="fixed left-0 top-0 z-50 hidden h-full w-64 flex-col border-r border-slate-200 bg-white lg:flex">
        <div className="border-b border-slate-100 px-6 py-5">
          <div className="flex items-center gap-3"><img src="/pin-clube-icon.png" alt="" className="h-11 w-11 rounded-lg" /><h1 className="font-heading text-2xl font-bold text-primary">{branding.name}</h1></div>
        </div>
        <nav aria-label="Menu principal do parceiro" className="flex-1 overflow-y-auto px-4 py-6 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                  isActive
                    ? "bg-primary text-white shadow-lg shadow-blue-500/20"
                    : "bg-orange-50 text-orange-800 hover:bg-orange-100 hover:text-orange-900"
                )
              }
            >
              <item.icon className="h-5 w-5" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
      </aside>
    </div>
  );
};

export default PartnerLayout;
